import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { Plus, Trash2, Upload, Save, Image as ImageIcon, Boxes } from "lucide-react";

const DEFAULT_COLOR = "#3B82F6";

export default function PlatformsManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState({}); // { [id]: { name?, color?, order? } }
  const [newForm, setNewForm] = useState({ name: "", color: "#22C55E", order: 100 });
  const fileRefs = useRef({});

  const refresh = async () => {
    try {
      const r = await api.get("/platforms");
      setItems(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => { (async () => { await refresh(); setLoading(false); })(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!newForm.name.trim()) { toast.error("Nome é obrigatório"); return; }
    try {
      await api.post("/platforms", {
        name: newForm.name.trim(),
        color: newForm.color || DEFAULT_COLOR,
        order: Number(newForm.order) || 100,
      });
      setNewForm({ name: "", color: "#22C55E", order: 100 });
      await refresh();
      toast.success("Plataforma criada");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const setField = (id, key, value) => {
    setPending((p) => ({ ...p, [id]: { ...(p[id] || {}), [key]: value } }));
  };

  const save = async (item) => {
    const patch = pending[item.id];
    if (!patch || Object.keys(patch).length === 0) { toast.info("Nada para guardar."); return; }
    const body = {
      name: patch.name ?? item.name,
      color: patch.color ?? item.color,
      order: Number(patch.order ?? item.order),
    };
    try {
      await api.put(`/platforms/${item.id}`, body);
      setPending((p) => { const n = { ...p }; delete n[item.id]; return n; });
      await refresh();
      toast.success("Plataforma atualizada");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (item) => {
    if (!window.confirm(`Eliminar "${item.name}"? Todas as atribuições a consultores serão removidas.`)) return;
    try {
      await api.delete(`/platforms/${item.id}`);
      await refresh();
      toast.success("Plataforma eliminada");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const uploadLogo = async (item, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/platforms/${item.id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success("Logo carregado");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="kw-card p-6 max-w-3xl" data-testid="platforms-manager">
      <div className="flex items-center gap-3 mb-5">
        <Boxes className="w-6 h-6 text-[#E50000]" />
        <div className="flex-1">
          <div className="kw-overline">Onda 2 do Diretório</div>
          <h2 className="font-display text-2xl font-black uppercase">Plataformas</h2>
          <p className="text-xs text-[#737373] mt-1">
            Gere as plataformas (Idealista Pro, Confidencial, Fizbot…). Atribuição por consultor faz-se no Diretório (admin ou staff com flag de gestor).
          </p>
        </div>
      </div>

      <form onSubmit={create} className="grid sm:grid-cols-4 gap-2 mb-5 items-end" data-testid="platform-create-form">
        <div className="sm:col-span-2">
          <label className="kw-overline text-[10px] block mb-1">Nome</label>
          <input className="kw-input w-full" value={newForm.name}
            onChange={(e) => setNewForm({ ...newForm, name: e.target.value })}
            placeholder="ex.: Casa Sapo Pro" data-testid="platform-new-name" />
        </div>
        <div>
          <label className="kw-overline text-[10px] block mb-1">Cor</label>
          <input type="color" className="kw-input w-full h-9 p-1" value={newForm.color}
            onChange={(e) => setNewForm({ ...newForm, color: e.target.value })}
            data-testid="platform-new-color" />
        </div>
        <button className="kw-btn-primary inline-flex items-center justify-center gap-2 h-9" type="submit" data-testid="platform-new-submit">
          <Plus className="w-4 h-4" /> Adicionar
        </button>
      </form>

      {loading ? (
        <p className="text-sm text-[#737373]">A carregar…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-[#737373] italic">Sem plataformas configuradas.</p>
      ) : (
        <div className="space-y-2">
          {items.map((item) => {
            const p = pending[item.id] || {};
            const dirty = Object.keys(p).length > 0;
            return (
              <div key={item.id} className="border border-[#222] p-3 flex flex-wrap items-center gap-3" data-testid={`platform-row-${item.id}`}>
                <div className="w-10 h-10 flex items-center justify-center border border-[#222] bg-[#0a0a0a]">
                  {item.logo_url ? (
                    <img src={item.logo_url} alt={item.name} className="max-w-full max-h-full object-contain" />
                  ) : (
                    <ImageIcon className="w-4 h-4 text-[#444]" />
                  )}
                </div>
                <input className="kw-input !py-1.5 !text-sm font-display font-black uppercase tracking-tight min-w-[180px] flex-1"
                  value={p.name ?? item.name}
                  onChange={(e) => setField(item.id, "name", e.target.value)}
                  data-testid={`platform-name-${item.id}`} />
                <input type="color" className="kw-input !py-0 !w-12 h-9 p-1"
                  value={p.color ?? item.color}
                  onChange={(e) => setField(item.id, "color", e.target.value)}
                  data-testid={`platform-color-${item.id}`} />
                <input type="number" className="kw-input !py-1.5 !text-xs !w-20"
                  value={p.order ?? item.order}
                  onChange={(e) => setField(item.id, "order", e.target.value)}
                  title="Ordem de exibição (menor primeiro)"
                  data-testid={`platform-order-${item.id}`} />
                <input
                  ref={(el) => { fileRefs.current[item.id] = el; }}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  className="hidden"
                  onChange={(e) => uploadLogo(item, e.target.files?.[0])}
                  data-testid={`platform-logo-input-${item.id}`} />
                <button type="button" onClick={() => fileRefs.current[item.id]?.click()}
                  className="kw-btn-secondary !py-1.5 inline-flex items-center gap-1 text-[10px]"
                  data-testid={`platform-logo-upload-${item.id}`}>
                  <Upload className="w-3 h-3" /> Logo
                </button>
                <button type="button" onClick={() => save(item)} disabled={!dirty}
                  className="kw-btn-primary !py-1.5 text-[10px] uppercase tracking-widest font-bold disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
                  data-testid={`platform-save-${item.id}`}>
                  <Save className="w-3 h-3" /> Guardar
                </button>
                <button type="button" onClick={() => remove(item)}
                  className="text-[10px] uppercase tracking-widest font-bold text-[#737373] hover:text-[#E50000] flex items-center gap-1"
                  data-testid={`platform-delete-${item.id}`}>
                  <Trash2 className="w-3 h-3" /> Eliminar
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
